import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';

interface ConfirmOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
}

interface ConfirmState {
  isOpen: boolean;
  message: string;
  title: string;
  confirmText: string;
  cancelText: string;
  type: 'danger' | 'warning' | 'info';
  resolve: ((value: boolean) => void) | null;
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
`;

const ModalCard = styled.div`
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 420px;
  max-width: 92vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div<{ $type: 'danger' | 'warning' | 'info' }>`
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-line);
  background-color: ${({ $type }) =>
    $type === 'danger' ? 'var(--color-error-bg)' :
    $type === 'warning' ? 'var(--color-warning-bg)' :
    'rgba(0, 218, 109, 0.08)'};
`;

const ModalTitle = styled.h3<{ $type: 'danger' | 'warning' | 'info' }>`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: ${({ $type }) =>
    $type === 'danger' ? 'var(--color-error)' :
    $type === 'warning' ? 'var(--color-warning)' :
    'var(--color-primary-dark)'};
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 1rem;
    color: ${({ $type }) =>
      $type === 'danger' ? 'var(--color-error)' :
      $type === 'warning' ? 'var(--color-warning)' :
      'var(--color-primary-dark)'};
  }
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-text-tertiary);
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: var(--color-surface);
    border-color: var(--color-text-secondary);
  }
`;



const ModalBody = styled.div`
  padding: 20px;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 12px 20px;
  border-top: 1px solid var(--color-line);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    message: '',
    title: 'Confirm',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'danger',
    resolve: null,
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((message, options = {}) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({
        isOpen: true,
        message,
        title: options.title || 'Confirm',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'danger',
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    resolveRef.current?.(true);
    setState(s => ({ ...s, isOpen: false }));
  };

  const handleCancel = () => {
    resolveRef.current?.(false);
    setState(s => ({ ...s, isOpen: false }));
  };

  useEffect(() => {
    if (!state.isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.isOpen]);

  const iconClass =
    state.type === 'danger' ? 'fas fa-exclamation-triangle' :
    state.type === 'warning' ? 'fas fa-exclamation-circle' :
    'fas fa-info-circle';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.isOpen && (
        <Backdrop onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}>
          <ModalCard>
            <ModalHeader $type={state.type}>
              <ModalTitle $type={state.type}>
                <i className={iconClass} />
                {state.title}
              </ModalTitle>
            </ModalHeader>
            <ModalBody>{state.message}</ModalBody>
            <ModalFooter>
              <CancelButton onClick={handleCancel}>
                {state.cancelText}
              </CancelButton>
              <button className={`btn ${state.type === 'danger' ? 'btn-danger' : 'btn-primary'}`} onClick={handleConfirm} autoFocus>
                {state.confirmText}
              </button>
            </ModalFooter>
          </ModalCard>
        </Backdrop>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
};
